import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Chip, Spinner } from '@nextui-org/react';
import { useGetAiWorkersByCategoryQuery, useDeleteAiWorkersMutation, useEntityResourcePermissions } from '@circuitry-ai/doc-data';
import {
  CardListViewMode,
  CtyButton,
  CtyShowToast,
  EntityCardWithActions,
  EntityListView,
  ListActionsCellWithDelete,
  checkType,
  getActionsColumnDef,
  getDescriptionColumnDef,
  getNameColumnDef,
} from '@circuitry-ai/doc-ui';
import { Icon } from '@iconify/react';
import { useCallback, useMemo, useState } from 'react';

interface UXGenieWorker {
  aiworker_id: string;
  name: string;
  description: string;
  status: string;
  category: string;
  type: string;
  sub_type: string;
  created_at: string;
  updated_at: string;
}

const TEMPLATES_TAB = 'templates';

interface UXGenieWorkerListProps {
  basePath?: string;
}

function useDeleteUXGenieHandler(worker: UXGenieWorker, refetch: () => void) {
  const [deleteAiWorkers, { isLoading: deleteUxgenieLoading }] = useDeleteAiWorkersMutation();

  const handleConfirmDelete = useCallback(async (closeModal: () => void) => {
    try {
      const response = await deleteAiWorkers(worker.aiworker_id);
      CtyShowToast(checkType.success, response?.data);
      closeModal();
      refetch();
    } catch (error) {
      CtyShowToast(checkType.error, (error as Error)?.message ?? 'Delete Operation failed');
    }
  }, [deleteAiWorkers, refetch, worker.aiworker_id]);

  return { handleConfirmDelete, isDeleteLoading: deleteUxgenieLoading };
}

const UXGenieWorkerCard = ({
  worker,
  basePath,
  refetch,
}: {
  readonly worker: UXGenieWorker;
  readonly basePath: string;
  readonly refetch: () => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canDelete } = useEntityResourcePermissions('uxgenie', worker.aiworker_id);
  const { handleConfirmDelete, isDeleteLoading } = useDeleteUXGenieHandler(worker, refetch);

  const footerItems = (
    <CtyButton
      className="w-full border-1"
      variant="solid"
      radius="sm"
      size="lg"
      color="primary"
      onPress={() =>
        navigate(`${basePath}/${worker.aiworker_id}?tab=${TEMPLATES_TAB}`, {
          state: { uxgenieWorker: worker },
        })
      }
    >
      {t('Launch')}
    </CtyButton>
  );

  const subType = worker.sub_type?.trim();

  return (
    <EntityCardWithActions
      id={worker.aiworker_id}
      name={worker.name}
      description={worker.description}
      bodyExtra={
        subType ? (
          <Chip size="sm" variant="flat" color="primary" className="w-fit max-w-full truncate">
            {subType}
          </Chip>
        ) : undefined
      }
      footerItems={footerItems}
      canDelete={canDelete}
      dropdownAriaLabel="UXGenie Actions"
      deleteModalEntityName={worker.name}
      onConfirmDelete={handleConfirmDelete}
      isDeleteLoading={isDeleteLoading}
      deleteConfirmLabel={t('Delete UXGenie template')}
    />
  );
};

const UXGenieActionsCell = ({ worker, basePath, refetch }: { readonly worker: UXGenieWorker; readonly basePath: string; readonly refetch: () => void }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canDelete } = useEntityResourcePermissions('uxgenie', worker.aiworker_id);
  const { handleConfirmDelete, isDeleteLoading } = useDeleteUXGenieHandler(worker, refetch);

  return (
    <ListActionsCellWithDelete
      actions={[
        {
          label: t('Launch'),
          onPress: () =>
            navigate(`${basePath}/${worker.aiworker_id}?tab=${TEMPLATES_TAB}`, {
              state: { uxgenieWorker: worker },
            }),
          variant: 'solid',
          color: 'primary',
        },
      ]}
      canDelete={canDelete}
      entityName={worker.name}
      onConfirmDelete={handleConfirmDelete}
      isDeleteLoading={isDeleteLoading}
      deleteConfirmLabel={t('Delete UXGenie template')}
    />
  );
};

const renderUXGenieActionsCell = (basePath: string, refetch: () => void) =>
  function UXGenieActionsRenderer(params: any) {
    return <UXGenieActionsCell worker={params?.data} basePath={basePath} refetch={refetch} />;
  };

export default function UXGenieWorkerList(props: Readonly<UXGenieWorkerListProps>) {
  const { t } = useTranslation();
  const basePath = props.basePath ?? '/uxgenie';
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [viewMode, setViewMode] = useState<CardListViewMode>(CardListViewMode.GRID);

  const { data: uxgenieWorkers, isLoading, isError, refetch } = useGetAiWorkersByCategoryQuery(
    { category: 'uxgenie' as any },
    { refetchOnMountOrArgChange: true }
  );

  const workers = useMemo(() => (uxgenieWorkers || []) as UXGenieWorker[], [uxgenieWorkers]);

  const filteredWorkers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return workers;
    return workers.filter((w) =>
      [w.name, w.description, w.sub_type].some((value) => value?.toLowerCase().includes(q))
    );
  }, [workers, searchQuery]);

  const columnDefs = useMemo(
    () => [
      getNameColumnDef(t('Name')),
      getDescriptionColumnDef(t('Description')),
      {
        headerName: t('SubType'),
        field: 'sub_type',
        sortable: true,
        minWidth: 140,
        flex: 0.7,
        filter: false,
        valueFormatter: (params: { value?: string }) => params?.value?.trim() || '-',
      },
      getActionsColumnDef({
        headerName: t('Actions'),
        minWidth: 200,
        maxWidth: 240,
        cellRenderer: renderUXGenieActionsCell(basePath, refetch),
      }),
    ],
    [t, basePath, refetch]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spinner size="sm" label={t('Loading UXGenie Templates...')} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-danger">{t('Error loading UXGenie Templates')}</div>
      </div>
    );
  }

  if (workers.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <Icon icon="mdi:magic-staff" className="text-default-400" width={64} height={64} />
        <p className="text-default-500">{t('No UXGenie Templates found')}</p>
      </div>
    );
  }

  return (
    <EntityListView
      items={filteredWorkers}
      isLoading={false}
      searchId="uxgenie-search"
      searchPlaceholder={t('Search UXGenie templates')}
      onSearch={setSearchQuery}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      emptyMessage={t('No UXGenie Templates found')}
      cardKey={(worker: UXGenieWorker) => worker.aiworker_id}
      renderCard={(worker: UXGenieWorker) => (
        <UXGenieWorkerCard worker={worker} basePath={basePath} refetch={refetch} />
      )}
      tableId="uxgenie-list-table"
      columnDefs={columnDefs}
    />
  );
}
